<template>
  <section class="c-hero">
    <div class="o-container">
      <h2>{{ $t('home.hero.title') }}</h2>
      <h3>{{ $t('home.hero.payoff') }}</h3>
    </div>
  </section>

  <section class="o-container py-20">
    <form class="root max-w-6xl" v-on:submit.prevent="submit">
      <h1 class="text-blue">{{ $t('home.title') }}</h1>
      <p class="text-lg font-semibold">{{ $t('home.payoff') }}</p>

      <div class="o-stack o-stack--lg mt-10">
        <label class="block max-w-3xl">
          <span class="field-label">{{
            $t("labels.name")
          }}</span>
          <input type="text" class="form-input mt-2 block w-full rounded text-blue" v-model="state.name" name="name"
            autofocus spellcheck="false" v-on:keydown.enter.prevent="focusOnEmail" enterkeyhint="next" tabindex="0"
            :class="{ 'invalid': v$.name.$error }" :placeholder="$t('placeholders.name')" />
          <span role="alert" v-for="error of v$.name.$errors" :key="error.$uid">
            {{ error.$message }}
          </span>
        </label>


        <label class="block max-w-3xl">
          <span class="field-label">{{
            $t("labels.email")
          }}</span>
          <input type="email" class="form-input mt-2 block w-full rounded text-blue" v-model="state.email"
            spellcheck="false" name="email" enterkeyhint="done" tabindex="0" :placeholder="$t('placeholders.email')"
            :class="{ 'invalid': v$.email.$error }" />
          <span role="alert" v-for="error of v$.email.$errors" :key="error.$uid">
            {{ error.$message }}
          </span>
        </label>

        <label class="block max-w-3xl">
          <span class="field-label">{{
            $t("labels.nameOfSchool")
          }}</span>
          <input type="text" class="form-input mt-2 block w-full" v-model="state.schoolName"
            v-on:keydown.enter.prevent="focusOnAddress" @input="onSchoolNameInput" enterkeyhint="next" name="schoolName" autofocus tabindex="0"
            :placeholder="$t('placeholders.nameOfSchool')" :class="{ 'invalid': v$.schoolName.$error }" />
          <span role="alert" v-for="error of v$.schoolName.$errors" :key="error.$uid">
            {{ error.$message }}
          </span>
        </label>


        <div class="max-w-3xl">

          <span class="field-label">{{
            $t("labels.schoolType")
          }}</span>
          <div>
            <label class="o-styled-checkbox mt-4">
              <input type="checkbox" name="schoolTypes" v-model="state.schoolTypes" value="DayCare" />
              {{ $t('types.organization.dayCare') }}
            </label>
            <label class="o-styled-checkbox mt-4">
              <input type="checkbox" name="schoolTypes" v-model="state.schoolTypes" value="PrimarySchool" />
              {{ $t('types.organization.primarySchool') }}
            </label>
            <span role="alert" v-for="error of v$.schoolTypes.$errors" :key="error.$uid">
              {{ error.$message }}
            </span>
          </div>
        </div>

        <div class="mt-[3rem] max-w-3xl">
          <AddressInput v-model="state.address" :errors="{
            street: v$.address.street.$errors,
            city: v$.address.city.$errors,
            postalCode: v$.address.postalCode.$errors,
            country: v$.address.country.$errors
          }" @autocomplete-selected="handleAddressAutocomplete" ref="addressInputRef"  class="o-stack o-stack--md" />

        </div>
        <div v-if="state.address.countryCode == 'DE'" class="o-panel mt-4 whitespace-pre-wrap w-full" v-html="gmbhDisclaimer">
        </div>

        <dialog ref="unsupportedCountryDialog">
          <p>{{ $t('home.unsupportedCountry') }}</p>
        </dialog>
        <label class="block mt-[3rem] max-w-3xl">
            <span class="field-label">
                {{
            $t("labels.website")
                }}
            </span>
            <input type="url" class="
                                                                form-input
                                                                mt-2
                                                                block
                                                                w-full
                                                                rounded
                                                                text-blue
                                                            " v-model="state.website"
                   :placeholder="$t('placeholders.website')" v-on:blur="checkUrl" @input="onWebsiteInput" name="website" tabindex="0"
                   :class="{ 'invalid': v$.website.$error }" />
            <span role="alert" v-for="error of v$.website.$errors" :key="error.$uid">
                {{ error.$message }}
            </span>
            <!--  <LocalizedErrorMessage name="website"></LocalizedErrorMessage>-->
        </label>

<!--        <label class="block mt-[3rem] max-w-3xl">-->
<!--          <span class="field-label">{{-->
<!--            $t("labels.promotionCode")-->
<!--          }}</span>-->
<!--          <input type="text" class="form-input mt-2 block w-full rounded text-blue" v-model="state.promotionCode"-->
<!--            :placeholder="$t('placeholders.promotionCode')" name="promotionCode" tabindex="0" />-->
<!--        </label>-->

        <div class="o-panel mt-10 text-sm">
          <h3 class="font-bold text-lg">{{ $t('labels.conditions') }}</h3>
          <p class="select-none">{{ $t('home.disclaimer') }}</p>


          <label class="o-styled-checkbox mt-4">
            <input type="checkbox" name="acceptedTermsAndConditions" v-model="state.acceptedTermsAndConditions"
              value="true" /><span v-html="termsAndConditionsText()"/>
          </label>
          <span role="alert" v-for="error of v$.acceptedTermsAndConditions.$errors" :key="error.$uid">
            {{ error.$message }}
          </span>
          <label class="o-styled-checkbox mt-4">
            <input type="checkbox" name="acceptedDataStorage" v-model="state.acceptedDataStorage" value="true" />
            {{ $t('labels.acceptDataStorage') }}
          </label>
          <span role="alert" v-for="error of v$.acceptedDataStorage.$errors" :key="error.$uid">
            {{ error.$message }}
          </span>

        </div>

        <p>{{ $t('home.afterSubmit') }}</p>

      </div>
      <!-- Honeypot field - hidden from users, bots will fill it -->
      <input type="text" name="EmailAddress" v-model="state.honeypot" autocomplete="off" tabindex="-1" />

      <v-button class="c-btn--primary mt-10 justify-self-start" type="submit" tabindex="0">{{
        $t("buttons.save")
      }}
      </v-button>
    </Form>
  </section>
</template>

<style lang="scss" scoped>
form.root {
  grid-area: main;
  display: grid;
  grid-template-rows: repeat(3, auto);
  align-items: start;
}

.logo {
  max-width: 88px;
}

input[name=EmailAddress] {
  opacity: 0;
  position: absolute;
  appearance: none;
  width: 0;
  height: 0;
}

.address-display {}
    label { cursor:pointer;}
</style>

<script>

import LocalizedErrorMessage from '../components/LocalizedErrorMessage.vue'
import AddressInput from '../components/AddressInput.vue'
import { ref, onMounted, watch, computed, reactive } from "vue";
import { Field, Form } from "vee-validate";
import axios from "axios";
import supportedCountries from '../supportedCountries.js';
import dialogPolyfill from 'dialog-polyfill'

import { useVuelidate } from '@vuelidate/core'
import { required, email, url, helpers } from '@vuelidate/validators'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { marked } from 'marked';
import * as validators from '@vuelidate/validators'

const supportedDomain = (value) => {
  return !/ziber\./.exec(value);
}


export default {
  components: {
    Field,
    Form,
    LocalizedErrorMessage,
    AddressInput
  },

  setup() {
    const {t:$t} = useI18n();
    const router = useRouter()

    const unsupportedCountryDialog = ref();

    const renderer = new marked.Renderer();

    renderer.link = function(href, title, text) {
          // Add rel="noopener" for security best practice
          const titleAttr = title ? ` title="${title}"` : '';
          return `<a href="${href}"${titleAttr} target="_blank" rel="noopener">${text}</a>`;
      };

    marked.setOptions({ renderer });

    const state = reactive({
      name: '',
      email: '',
      schoolName: '',
      schoolTypes: [],
      language: localStorage.getItem("locale"),
      address: {
        street: '',
        city: '',
        postalCode: '',
        country: '',
        countryCode: '',
        location: {
          lat: null,
          lng: null
        }
      },
      website: '',
      promotionCode: '',
      acceptedTermsAndConditions: null,
      acceptedDataStorage: null,
      honeypot: ''
    })

    const rules = {
      name: { required: helpers.withMessage(() => $t('validation.required'), required) },
      email: {
          required: helpers.withMessage(() => $t('validation.required'), required),
          email: helpers.withMessage(() => $t('validation.email'), email) },
      schoolName: { required: helpers.withMessage(() => $t('validation.required'), required) },
      website: {
          required: helpers.withMessage(() => $t('validation.required'), required),
          url: helpers.withMessage(() => $t('validation.url'), url),
          supportedDomain : helpers.withMessage(() => $t('validation.invalidDomain'), supportedDomain)
      },
      address: {
        street: { required: helpers.withMessage(() => $t('validation.required'), required) },
        city: { required: helpers.withMessage(() => $t('validation.required'), required) },
        postalCode: { required: helpers.withMessage(() => $t('validation.required'), required) },
        country: { },
        countryCode: { required }
      },
      schoolTypes: { required: helpers.withMessage(() => $t('validation.required'), required) },
      acceptedTermsAndConditions: { required: helpers.withMessage(() => $t('validation.required'), required) },
      acceptedDataStorage: { required: helpers.withMessage(() => $t('validation.required'), required) },
    }

    const addressInputRef = ref();
    const schoolNameTouched = ref(false);
    const websiteTouched = ref(false);

    const focusOnEmail = () => {
      document.querySelector('[name=email]').focus();
    }

    const focusOnAddress = () => {
      setTimeout(() => {
        addressInputRef.value?.focus();
      }, 0)
    }

    const supportedCountry = computed(() => {
      if (!state.address.countryCode) return true;

      return supportedCountries.indexOf(state.address.countryCode) !== -1;
    });
    watch(supportedCountry, (isSupported) => {
      if (!isSupported) {
        unsupportedCountryDialog.value.showModal();
      }
    });

    const gmbhDisclaimer = computed(() => {
      return marked.parse($t('home.ziberGmbhDisclaimer'), {
        breaks: true
      });
    })

    const handleAddressAutocomplete = (data) => {
      const { hasStreetNumber, place } = data;

      // Only auto-fill school name if the user hasn't touched it
      if (!schoolNameTouched.value) {
        // Only auto-fill school name if the place is actually an establishment/organization
        // Check if place.types includes establishment-related types (not just a street/route)
        const isEstablishment = place.types?.some(type =>
          ['establishment', 'school', 'point_of_interest', 'premise'].includes(type)
        ) && !place.types?.includes('route');

        // Update school name if the place is an establishment
        if (place.name?.length && isEstablishment) {
          state.schoolName = place.name;
        } else {
          // Clear school name if it's just a street address
          state.schoolName = '';
        }
      }

      // Only auto-fill website if the user hasn't touched it
      if (!websiteTouched.value) {
        // Update website if available
        if (place.website?.length) {
          state.website = place.website;
        } else {
          // Clear website if not available
          state.website = '';
        }
      }

      // Focus on website field after address selection
      document.querySelector('[name=website]')?.focus();
    }

    onMounted(() => {
      // focus on the first element
      document.querySelector('[name=name]').focus();

      dialogPolyfill.registerDialog(unsupportedCountryDialog.value);
    });

    const checkUrl = () => {

      if (!state.website) return;

      if (!state.website.match(/^https?:/) && state.website.length) {
        state.website = "https://" + state.website;
      }
    }

    const onSchoolNameInput = () => {
      schoolNameTouched.value = true;
    }

    const onWebsiteInput = () => {
      websiteTouched.value = true;
    }

    const termsAndConditionsText = () => {
          return marked.parse($t('labels.acceptTermsAndConditions'));
      }

    const v$ = useVuelidate(rules, state)

    const submit = async () => {
      const result = await v$.value.$validate()

      if (result) {
        // Map the nested address structure to the backend format
        const payload = {
          name: state.name,
          email: state.email,
          schoolName: state.schoolName,
          schoolTypes: state.schoolTypes,
          language: state.language,
          website: state.website,
          promotionCode: state.promotionCode,
          // Flatten address structure for backend
          addressLine1: state.address.street,
          city: state.address.city,
          postalCode: state.address.postalCode,
          countryCode: state.address.countryCode,
          location: {
            Lat: state.address.location.lat,
            Lng: state.address.location.lng
          },
          // Honeypot for spam protection
          emailAddress: state.honeypot
        };

        await axios.post('', payload)
        router.push({ name: 'finished' });
      }
    }

    return {
      v$,
      addressInputRef,
      state,
      checkUrl,
      focusOnEmail,
      focusOnAddress,
      handleAddressAutocomplete,
      onSchoolNameInput,
      onWebsiteInput,
      submit,
      gmbhDisclaimer,
      unsupportedCountryDialog,
      termsAndConditionsText
    };
  },
};
</script>